import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const GITHUB_USERNAME = 'smitnikumbh';  // ← ADD THIS BACK
const PHOTO_URL = `${process.env.PUBLIC_URL}/smit.jpg`;
// And add this for schema/og tags:
const PHOTO_URL_ABSOLUTE = 'https://www.focusdenapp.com/smit.jpg';

const Typewriter = ({ words, speed = 75, pause = 1800 }) => {
  const [displayed, setDisplayed] = useState('');
  const [wordIdx, setWordIdx]     = useState(0);
  const [charIdx, setCharIdx]     = useState(0);
  const [deleting, setDeleting]   = useState(false);
  useEffect(() => {
    const current = words[wordIdx];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplayed(current.slice(0, charIdx + 1));
        if (charIdx + 1 === current.length) setTimeout(() => setDeleting(true), pause);
        else setCharIdx(c => c + 1);
      } else {
        setDisplayed(current.slice(0, charIdx - 1));
        if (charIdx - 1 === 0) { setDeleting(false); setWordIdx(i => (i + 1) % words.length); setCharIdx(0); }
        else setCharIdx(c => c - 1);
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);
  return <span>{displayed}<span className="animate-pulse text-indigo-400">|</span></span>;
};

const Counter = ({ end, suffix = '', decimals = 0, duration = 2000 }) => {
  const [val, setVal]         = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    let t;
    const animate = ts => {
      if (!t) t = ts;
      const p = Math.min((ts - t) / duration, 1);
      setVal(decimals > 0 ? (p * end).toFixed(decimals) : Math.floor(p * end));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [visible, end, duration, decimals]);
  return <span ref={ref}>{typeof val === 'number' ? val.toLocaleString() : val}{suffix}</span>;
};

const TimelineItem = ({ year, title, desc, icon, last }) => (
  <div className="relative flex gap-6 pb-10">
    {!last && <div className="absolute left-[19px] top-10 w-0.5 h-full bg-gradient-to-b from-indigo-500/40 to-transparent" />}
    <div className="w-10 h-10 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-lg shrink-0 z-10">{icon}</div>
    <div className="pt-1.5">
      <span className="text-xs font-semibold text-indigo-400 tracking-widest uppercase">{year}</span>
      <h4 className="text-base font-bold text-white mt-0.5 mb-1">{title}</h4>
      <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const Skill = ({ label, icon, highlight }) => (
  <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-default
    ${highlight
      ? 'bg-indigo-500/15 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/25'
      : 'bg-white/5 border border-white/10 text-gray-300 hover:border-indigo-500/30 hover:text-white'}`}>
    <span>{icon}</span><span>{label}</span>
  </div>
);

const SNAvatar = () => (
  <div className="w-full h-full flex items-center justify-center relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
    <div className="absolute w-72 h-72 rounded-full border border-indigo-500/10" />
    <div className="absolute w-52 h-52 rounded-full border border-indigo-500/15" />
    <div className="absolute w-36 h-36 rounded-full border border-indigo-500/20" />
    <div className="absolute w-32 h-32 rounded-full blur-[40px]"
      style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, transparent 70%)' }} />
    <div className="relative z-10 flex items-end gap-0.5 select-none">
      <span className="text-7xl font-extrabold leading-none tracking-tighter"
        style={{
          background: 'linear-gradient(135deg, #818cf8 0%, #a78bfa 40%, #f472b6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 0 20px rgba(99,102,241,0.6))',
        }}>
        SN
      </span>
    </div>
    <div className="absolute top-6 left-6 w-1.5 h-1.5 rounded-full bg-indigo-400/40" />
    <div className="absolute top-6 right-6 w-1.5 h-1.5 rounded-full bg-pink-400/40" />
    <div className="absolute bottom-6 left-6 w-1.5 h-1.5 rounded-full bg-pink-400/40" />
    <div className="absolute bottom-6 right-6 w-1.5 h-1.5 rounded-full bg-indigo-400/40" />
  </div>
);

export default function AboutFounder() {
  const [scrollY, setScrollY]               = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [imgLoaded, setImgLoaded]           = useState(false);
  const [imgError, setImgError]             = useState(false);

  useEffect(() => {
    const fn = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = PHOTO_URL;
    img.onload  = () => setImgLoaded(true);
    img.onerror = () => setImgError(true);
  }, []);

  const typewriterWords = [
    'Founder of FocusDen',
    'Android Developer',
    'Full-Stack Developer',
    'AWS & DevOps Engineer',
    'Open to Freelance Projects',
  ];

  const timeline = [
    { year: '2022', icon: '💡', title: 'The idea', desc: "Struggled with phone distractions during studies. Couldn't find a proper scheduled app-blocker that actually worked on Indian Android phones — so built one." },
    { year: '2023', icon: '🛠️', title: 'Launched FocusDen', desc: 'Shipped DistraX (renamed FocusDen) in Kotlin & Java. Published on Google Play under Supnistiq Labs. First real users, first real feedback.' },
    { year: '2024', icon: '🚀', title: 'Scaled the app', desc: 'Added Strict Mode, Super Focus with Pomodoro + binaural beats, AES-256 premium security, full analytics. Crossed 10K+ downloads.' },
    { year: '2025', icon: '🌍', title: 'Expanding skills', desc: 'Deepened full-stack + AWS/DevOps skills alongside FocusDen. Building Supnistiq Labs into a serious product studio. Open to freelance.' },
  ];

  const skillGroups = [
    {
      label: 'Mobile',
      skills: [
        { label: 'Android (Kotlin)', icon: '📱', highlight: true },
        { label: 'Java', icon: '☕', highlight: true },
        { label: 'Material Design', icon: '✨', highlight: false },
        { label: 'Accessibility Services', icon: '♿', highlight: false },
        { label: 'Room Database', icon: '🗄️', highlight: false },
      ]
    },
    {
      label: 'Web & Frontend',
      skills: [
        { label: 'React.js', icon: '⚛️', highlight: true },
        { label: 'JavaScript', icon: '🟨', highlight: false },
        { label: 'HTML / CSS', icon: '🎨', highlight: false },
        { label: 'Tailwind CSS', icon: '💨', highlight: false },
      ]
    },
    {
      label: 'Cloud & DevOps',
      skills: [
        { label: 'AWS', icon: '☁️', highlight: true },
        { label: 'DevOps', icon: '⚙️', highlight: true },
        { label: 'Firebase', icon: '🔥', highlight: false },
        { label: 'CI/CD', icon: '🔄', highlight: false },
      ]
    },
    {
      label: 'Product & Business',
      skills: [
        { label: 'Project Management', icon: '📋', highlight: true },
        { label: 'Play Store ASO', icon: '📈', highlight: false },
        { label: 'App Monetisation', icon: '💰', highlight: false },
        { label: 'Freelancing', icon: '🤝', highlight: true },
        { label: 'Product Design', icon: '🖌️', highlight: false },
      ]
    },
  ];

  const stats = [
    { val: 10,  suffix: 'K+', label: 'App Downloads',    icon: '⬇️' },
    { val: 4.8, dec: 1, suffix: '★', label: 'Play Store Rating', icon: '⭐' },
    { val: 3,   suffix: '+',  label: 'Years Building',   icon: '🛠️' },
    { val: 10,  suffix: 'M+', label: 'Hours Saved',      icon: '⏱️' },
  ];

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Smit Nikumbh",
    "givenName": "Smit",
    "familyName": "Nikumbh",
    "jobTitle": "Full-Stack Developer & Founder",
    "description": "Smit Nikumbh is a Full-Stack Developer and Founder of FocusDen, an Android productivity app with 10K+ downloads. Specialises in Android (Kotlin/Java), React, AWS, DevOps, and Project Management. Based in Pune, India. Open to freelance projects.",
    "url": "https://www.focusdenapp.com/about",
    "image": { "@type": "ImageObject", "url": PHOTO_URL_ABSOLUTE, "description": "Smit Nikumbh – Founder of FocusDen" },
    "worksFor": { "@type": "Organization", "name": "Supnistiq Labs", "url": "https://www.focusdenapp.com" },
    "founder": [
      { "@type": "Organization", "name": "FocusDen",      "url": "https://www.focusdenapp.com" },
      { "@type": "Organization", "name": "Supnistiq Labs", "url": "https://www.focusdenapp.com" }
    ],
    "knowsAbout": ["Android Development","Kotlin","Java","React.js","Full-Stack Development","AWS","DevOps","Project Management","App Monetization","Digital Wellbeing","Google Play Store","Freelancing"],
    "nationality": { "@type": "Country", "name": "India" },
    "address": { "@type": "PostalAddress", "addressLocality": "Pune", "addressCountry": "IN" },
    "sameAs": [
      `https://github.com/${GITHUB_USERNAME}`,
      "https://www.linkedin.com/in/smit-nikumbh/",
      "https://play.google.com/store/apps/developer?id=Supnistiq+Labs"
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",          "item": "https://www.focusdenapp.com/" },
      { "@type": "ListItem", "position": 2, "name": "About Founder", "item": "https://www.focusdenapp.com/about" }
    ]
  };

  return (
    <div className="font-['Outfit'] bg-[#0A0A14] text-gray-50 min-h-screen relative overflow-x-hidden">
      <Helmet>
        <title>Smit Nikumbh – Full-Stack Developer & Founder of FocusDen | Pune, India</title>
        <meta name="description" content="Smit Nikumbh is a Full-Stack Developer, Android Developer, and Founder of FocusDen (10K+ downloads). Kotlin, React, AWS & DevOps. Pune, India. Open to freelance projects." />
        <meta name="author" content="Smit Nikumbh" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.focusdenapp.com/about" />
        <meta property="og:title"       content="Smit Nikumbh – Full-Stack Developer & Founder of FocusDen" />
        <meta property="og:description" content="Full-Stack Dev, Android, AWS & DevOps. Founder of FocusDen — 10K+ downloads. Open to freelance. Pune, India." />
        <meta property="og:url"         content="https://www.focusdenapp.com/about" />
        <meta property="og:type"        content="profile" />
        <meta property="og:image"       content={PHOTO_URL_ABSOLUTE} />
        <meta property="og:image:alt"   content="Smit Nikumbh – Founder of FocusDen" />
        <meta property="profile:first_name" content="Smit" />
        <meta property="profile:last_name"  content="Nikumbh" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="Smit Nikumbh – Full-Stack Developer & Founder of FocusDen" />
        <meta name="twitter:description" content="Full-Stack Dev, Android, AWS & DevOps. Founder of FocusDen — 10K+ downloads on Google Play." />
        <meta name="twitter:image"       content={PHOTO_URL_ABSOLUTE} />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
        html { scroll-behavior: smooth; }
        @keyframes float       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes fadeUp      { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gradient    { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes photoReveal { from{opacity:0;transform:scale(1.04)} to{opacity:1;transform:scale(1)} }
        @keyframes borderGlow  { 0%,100%{box-shadow:0 0 0 1px rgba(99,102,241,.3),0 0 40px rgba(99,102,241,.2)} 50%{box-shadow:0 0 0 1px rgba(167,139,250,.5),0 0 60px rgba(167,139,250,.3)} }
        .animate-gradient  { background-size:200% auto; animation:gradient 4s ease infinite; }
        .animate-float     { animation:float 4s ease-in-out infinite; }
        .fade-up-1 { animation:fadeUp .55s ease-out .05s both; }
        .fade-up-2 { animation:fadeUp .55s ease-out .18s both; }
        .fade-up-3 { animation:fadeUp .55s ease-out .32s both; }
        .fade-up-4 { animation:fadeUp .55s ease-out .46s both; }
        .photo-reveal { animation:photoReveal .7s ease-out both; }
        .avatar-frame { animation:borderGlow 3s ease-in-out infinite; }
        .skill-group-label { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:rgba(148,163,184,.6); margin-bottom:10px; }
      `}</style>

      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute w-[700px] h-[500px] -top-[10%] -left-[10%] rounded-full blur-[110px]"
          style={{ background:'radial-gradient(circle,rgba(99,102,241,.25) 0%,transparent 70%)', transform:`translateY(${scrollY*.1}px)` }} />
        <div className="absolute w-[450px] h-[450px] top-[40%] -right-[8%] rounded-full blur-[110px]"
          style={{ background:'radial-gradient(circle,rgba(236,72,153,.15) 0%,transparent 70%)', transform:`translateY(${scrollY*-.1}px)` }} />
        <div className="absolute w-[350px] h-[350px] bottom-[10%] left-[20%] rounded-full blur-[100px]"
          style={{ background:'radial-gradient(circle,rgba(34,211,238,.1) 0%,transparent 70%)', transform:`translateY(${scrollY*.07}px)` }} />
      </div>

      {/* NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-4 backdrop-blur-xl transition-all duration-300 ${scrollY>50?'bg-[#0A0A14]/95 border-b border-white/5':'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="FocusDen logo" className="w-10 h-10" />
            <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent">FocusDen</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
            <Link to="/#pricing"  className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</Link>
            <Link to="/about"     className="text-white text-sm font-semibold border-b-2 border-indigo-500 pb-0.5">About</Link>
            <a href="https://play.google.com/store/apps/details?id=com.supnistic_ai.distraX" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:scale-105 transition-all">
              Download Free
            </a>
          </div>
          <button className="md:hidden flex flex-col gap-1.5 p-2" onClick={()=>setMobileMenuOpen(!mobileMenuOpen)}>
            {[1,2,3].map(i=><span key={i} className="w-6 h-0.5 bg-white rounded"/>)}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden fixed top-[70px] left-0 right-0 bg-[#0A0A14] backdrop-blur-xl p-8 flex flex-col gap-6 border-t border-white/10 overflow-hidden z-40">
            <Link to="/#features" onClick={()=>setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium">Features</Link>
            <Link to="/#pricing"  onClick={()=>setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium">Pricing</Link>
            <Link to="/about"     onClick={()=>setMobileMenuOpen(false)} className="text-white text-lg font-bold">About</Link>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="min-h-screen flex items-center px-4 md:px-8 pt-32 pb-20 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center w-full">

          <div className="order-2 lg:order-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-6 fade-up-1">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
              Founder & Full-Stack Developer
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-4 leading-none tracking-tight fade-up-2">
              Hi, I'm{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient block sm:inline">
                Smit Nikumbh
              </span>
            </h1>
            <div className="text-xl text-gray-300 font-semibold mb-4 h-8 fade-up-3">
              <Typewriter words={typewriterWords}/>
            </div>
            <p className="text-base text-gray-400 leading-relaxed mb-4 max-w-lg mx-auto lg:mx-0 fade-up-3">
              I build Android apps and web applications that genuinely help people. FocusDen started because I couldn't find
              a proper app-blocker for Indian phones — so I built one.{' '}
              <span className="text-white font-medium">10K+ downloads</span> later, I'm still shipping solo from Pune, India.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/25 px-4 py-2 rounded-full text-sm text-green-300 mb-8 fade-up-3">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
              Open to Freelance Projects
            </div>
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start fade-up-4">
              <a href="https://www.linkedin.com/in/smit-nikumbh/" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0077B5]/20 border border-[#0077B5]/40 text-[#5BB8F5] px-5 py-3 rounded-xl text-sm font-semibold hover:bg-[#0077B5]/30 transition-all">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </a>
              <a href={`https://github.com/${GITHUB_USERNAME}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-gray-300 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 hover:border-white/30 transition-all">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
                GitHub
              </a>
              <a href="mailto:official.supnistic.ai@gmail.com"
                className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-indigo-500/20 transition-all">
                ✉️ Email Me
              </a>
              <a href="https://play.google.com/store/apps/developer?id=Supnistiq+Labs" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/25 text-green-300 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-green-500/20 transition-all">
                ▶ Play Store
              </a>
            </div>
          </div>

          {/* Photo */}
          <div className="order-1 lg:order-2 flex justify-center relative">
            <div className="relative">
              <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-[36px] overflow-hidden avatar-frame border border-white/10">
                {imgLoaded && !imgError ? (
                  <img src={PHOTO_URL} alt="Smit Nikumbh – Full-Stack Developer and Founder of FocusDen, Pune India"
                    className="w-full h-full object-cover photo-reveal"  />
                ) : (
                  <SNAvatar/>
                )}
              </div>
            
              <div className="absolute -top-5 -right-4 sm:-right-8 bg-[#0F0F1A]/95 backdrop-blur-xl border border-white/10 px-3 py-2 rounded-xl text-xs font-medium shadow-xl animate-float">🇮🇳 Pune, India</div>
              <div className="absolute top-[42%] -right-4 sm:-right-14 hidden sm:flex bg-[#0F0F1A]/95 backdrop-blur-xl border border-indigo-500/20 px-3 py-2 rounded-xl text-xs font-medium shadow-xl animate-float" style={{animationDelay:'1s'}}>📱 10K+ Downloads</div>
              <div className="absolute -bottom-10 -left-4 sm:-left-10 bg-[#0F0F1A]/95 backdrop-blur-xl border border-white/10 px-3 py-2 rounded-xl text-xs font-medium shadow-xl animate-float" style={{animationDelay:'2s'}}>🎯 Founder, FocusDen</div>
              <div className="absolute top-4 -left-4 sm:-left-12 hidden sm:flex bg-[#0F0F1A]/95 backdrop-blur-xl border border-green-500/20 px-3 py-2 rounded-xl text-xs font-medium text-green-300 shadow-xl animate-float" style={{animationDelay:'1.5s'}}>🤝 Open to Freelance</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-4 md:px-8 py-10 relative z-10">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s,i)=>(
            <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-6 text-center hover:border-indigo-500/30 transition-all group">
              <div className="text-3xl mb-1 group-hover:scale-110 transition-transform">{s.icon}</div>
              <div className="text-3xl font-extrabold bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent">
                <Counter end={s.val} suffix={s.suffix} decimals={s.dec||0}/>
              </div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STORY + TIMELINE */}
      <section className="px-4 md:px-8 py-20 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">📖 My Story</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-12 leading-tight">Why I Built FocusDen</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-5 text-gray-400 leading-relaxed">
              <p>I was struggling to focus during my studies. My phone kept pulling me away — Instagram, YouTube, WhatsApp. I looked for a proper scheduled app-blocker but everything either didn't work on Indian Android devices or was too basic.</p>
              <p>So I built it myself. What started as a personal tool became <strong className="text-white">FocusDen</strong> — published under my studio <strong className="text-white">Supnistiq Labs</strong> on Google Play. The response was beyond what I expected.</p>
              <p>Alongside FocusDen, I've grown into a full-stack developer — Android, React, AWS, DevOps. I like building things end-to-end, from the APK on your phone to the cloud infrastructure behind it.</p>
              <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-2xl p-5 text-sm italic text-gray-300">
                "I'm not just a developer — I'm the first user. Every feature I ship is something I personally needed."
                <div className="text-indigo-400 font-semibold mt-2 not-italic">— Smit Nikumbh</div>
              </div>
            </div>
            <div className="pl-2">
              {timeline.map((item,i)=><TimelineItem key={i} {...item} last={i===timeline.length-1}/>)}
            </div>
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section className="px-4 md:px-8 py-20 relative z-10 bg-gradient-to-b from-indigo-500/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">⚡ Skills & Stack</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-10">What I Work With</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {skillGroups.map((group,i)=>(
              <div key={i}>
                <div className="skill-group-label">{group.label}</div>
                <div className="flex flex-wrap gap-2">
                  {group.skills.map((s,j)=><Skill key={j} {...s}/>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUSDEN CARD */}
      <section className="px-4 md:px-8 py-20 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">🎯 My App</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-10">FocusDen — My Main Project</h2>
          <div className="bg-gradient-to-br from-indigo-500/10 to-purple-600/5 border border-indigo-500/20 rounded-3xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="FocusDen app icon" className="w-20 h-20 rounded-3xl shrink-0"/>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">FocusDen – App Blocker for Android</h3>
                <p className="text-gray-400 leading-relaxed mb-6">An Android productivity app helping students and professionals block distracting apps on a schedule. Task-based blocking, Strict Mode, Super Focus Mode with Pomodoro + binaural beats, and a full analytics dashboard. Published under <strong className="text-white">Supnistiq Labs</strong> on Google Play.</p>
                <div className="flex flex-wrap gap-3">
                  <a href="https://play.google.com/store/apps/details?id=com.supnistic_ai.distraX" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-2xl text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:scale-105 transition-transform">
                    ▶ View on Play Store
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white px-6 py-3 rounded-2xl text-sm font-medium hover:bg-white/10 transition-all">
                    🌐 Visit Website
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREELANCE CTA */}
      <section className="px-4 md:px-8 py-24 text-center relative z-10 bg-[radial-gradient(ellipse_at_center,_rgba(99,102,241,0.13)_0%,_transparent_70%)]">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/25 px-4 py-2 rounded-full text-sm text-green-300 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
            Available for Freelance
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">Let's Build Something Together</h2>
          <p className="text-lg text-gray-400 mb-10 leading-relaxed">Android app, web app, AWS infra — if you need a full-stack developer who ships, let's talk.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:official.supnistic.ai@gmail.com"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl shadow-indigo-500/30 hover:scale-105 transition-transform">
              ✉️ Get in Touch
            </a>
            <a href="https://www.linkedin.com/in/smit-nikumbh/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl font-medium hover:bg-white/10 transition-all">
              💼 LinkedIn Profile
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-4 md:px-8 py-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">🎯</span>
                <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent">FocusDen</span>
              </div>
              <p className="text-gray-500 max-w-xs leading-relaxed text-sm">
                Built by <strong className="text-gray-400">Smit Nikumbh</strong> under Supnistiq Labs.
              </p>
            </div>
            <div className="flex gap-12">
              <div>
                <h4 className="font-semibold mb-4 text-sm">Product</h4>
                <div className="flex flex-col gap-3">
                  <Link to="/#features" className="text-gray-500 hover:text-white text-sm transition-colors">Features</Link>
                  <Link to="/#pricing"  className="text-gray-500 hover:text-white text-sm transition-colors">Pricing</Link>
                  <a href="https://play.google.com/store/apps/details?id=com.supnistic_ai.distraX" className="text-gray-500 hover:text-white text-sm transition-colors">Download</a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-sm">Connect</h4>
                <div className="flex flex-col gap-3">
                  <a href="https://www.linkedin.com/in/smit-nikumbh/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors">LinkedIn</a>
                  <a href={`https://github.com/${GITHUB_USERNAME}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors">GitHub</a>
                  <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
                  <a href="mailto:official.supnistic.ai@gmail.com" className="text-gray-500 hover:text-white text-sm transition-colors">Contact</a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 text-center text-gray-500 text-sm">
            <p className="mb-1">© 2026 FocusDen by <strong className="text-gray-400">Smit Nikumbh</strong>. All rights reserved. Made with ❤️ in Pune, India</p>
            <p className="text-xs text-gray-600 mt-2">
              Smit Nikumbh • Founder FocusDen • Android Developer • Full-Stack Developer • AWS DevOps • Freelance Developer India • Pune • Supnistiq Labs
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}